import React from "react";
import {
  Box,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { SystemStyleObject } from "@mui/system";

export interface InsightsToggleOption<T extends string> {
  id: T;
  label: string;
  icon?: string;
}

interface InsightsToggleGroupProps<T extends string> {
  label: string;
  value: T;
  options: ReadonlyArray<InsightsToggleOption<T>>;
  onChange: (nextValue: T) => void;
  compact?: boolean;
  wrapperSx?: SystemStyleObject<Theme>;
  groupSx?: SystemStyleObject<Theme>;
}

const BASE_WRAPPER_SX = {
  maxWidth: "100%",
  overflowX: "auto",
  pb: 0.5,
} as const;

function getGroupSx(compact: boolean): SxProps<Theme> {
  return {
    display: "inline-flex",
    minWidth: "max-content",
    flexWrap: "nowrap",
    gap: compact ? 0.5 : 0.75,
    p: compact ? 0.5 : 0.75,
    borderRadius: compact ? "0.875rem" : "1rem",
    border: "1px solid var(--color-border)",
    backgroundColor: "var(--color-bg-secondary)",
    boxShadow: "var(--shadow-card)",
    "& .MuiToggleButtonGroup-grouped": {
      m: 0,
      border: 0,
    },
    "& .MuiToggleButtonGroup-firstButton, & .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton": {
      borderRadius: compact ? "0.75rem" : "0.875rem",
    },
    "& .MuiToggleButton-root": {
      display: "inline-flex",
      alignItems: "center",
      gap: compact ? 0.5 : 0.75,
      px: compact ? 1.5 : 2.25,
      py: compact ? 0.875 : 1.25,
      color: "var(--color-text-secondary)",
      border: "1px solid transparent",
      borderRadius: compact ? "0.75rem" : "0.875rem",
      fontSize: compact ? "0.75rem" : "0.875rem",
      fontWeight: 700,
      letterSpacing: "0.01em",
      lineHeight: 1.2,
      textTransform: "none",
      whiteSpace: "nowrap",
      transition:
        "background-color 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
    },
    "& .MuiToggleButton-root:hover": {
      backgroundColor: "var(--color-bg-card)",
      color: "var(--color-text-primary)",
    },
    "& .MuiToggleButton-root.Mui-selected": {
      backgroundColor: "var(--color-accent-indigo)",
      color: "var(--color-badge-text)",
      borderColor: "var(--color-accent-indigo)",
      boxShadow: "var(--shadow-lg)",
    },
    "& .MuiToggleButton-root.Mui-selected:hover": {
      backgroundColor: "var(--color-accent-indigo)",
    },
    "& .MuiToggleButton-root.Mui-focusVisible": {
      outline: "2px solid var(--color-accent)",
      outlineOffset: 2,
    },
  };
}

function InsightsToggleGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  compact = false,
  wrapperSx,
  groupSx,
}: InsightsToggleGroupProps<T>) {
  const resolvedWrapperSx = wrapperSx
    ? ([BASE_WRAPPER_SX, wrapperSx] as SxProps<Theme>)
    : BASE_WRAPPER_SX;
  const resolvedGroupSx = groupSx
    ? ([getGroupSx(compact), groupSx] as SxProps<Theme>)
    : getGroupSx(compact);

  return (
    <Box sx={resolvedWrapperSx}>
      <ToggleButtonGroup
        value={value}
        exclusive
        aria-label={label}
        onChange={(_event, nextValue: T | null) => {
          if (!nextValue) return;
          onChange(nextValue);
        }}
        sx={resolvedGroupSx}
      >
        {options.map((option) => (
          <ToggleButton key={option.id} value={option.id}>
            {option.icon ? <span aria-hidden>{option.icon}</span> : null}
            <span>{option.label}</span>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

export default InsightsToggleGroup;
